import { GetStaticPropsContext } from 'next';
import {
  getFilterOptions,
  getFooterLinks,
  getLaunchesAreas
} from '../../../../../new-lib/apis';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Breadcrumbs from '../../../../../new-components/Breadcrumbs';
import styled from 'styled-components';
import AutoColumnsGrid from '../../../../../new-components/display-system/AutoColumnsGrid';
import { useTranslation } from 'next-i18next';
import LaunchPageCard from '../../../../../features/entity/launch/components/LaunchPageCard/LaunchPageCard';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import CTA from '../../../../../new-components/ui/CTA';
import { EntityTypes } from '../../../../../new-lib/constants/common';
import LowerDetails from '../../../../../features/entity/components/entity-details/mobile/LowerDetails';
import DescriptionBlock from '../../../../../new-components/DescriptionBlock';
import { useState } from 'react';
import useFetchLaunches from '../../../../../features/entity/launch/hooks/FetchLaunches';
import LoadMoreContainer from '../../../../../new-components/load-more/LoadMoreContainer';
import TrendingProjects from '../../../../../features/entity/launch/components/TrendingProjects/TrendingProjects';
import LaunchAreaFilters from '../../../../../features/entity/launch/components/LaunchAreaFilters/LaunchAreaFilters';
import { logEvent } from '../../../../../new-lib/analytics';
import { getPageLocation } from '../../../../../helpers/common';
import {
  setExperimentVariant,
  setIsMobile,
  setWebview,
  WEBVIEW
} from '../../../../../redux/actions/appActions';
import { wrapper } from '../../../../../redux/store';
import {
  LaunchDTO,
  LaunchesResponseDTO
} from '../../../../../features/entity/launch/dtos/response.dtos';
import { ConfigsApis } from '../../../../../new-lib/apis/configs.apis';
import { LaunchesApis } from '../../../../../new-lib/apis/launches.apis';
import { StaticPagesUtility } from '../../../../../new-lib/utilities/static-pages.utility';

interface ContainerProps {
  webview?: boolean;
}

interface LaunchesProps {
  launches: LaunchesResponseDTO;
  areas: { id: number; name: string }[];
  trendingProjects: any[];
}

const Launches = ({
  launches,
  areas,
  trendingProjects
}: LaunchesProps): JSX.Element => {
  const { t } = useTranslation('launches');

  const { locale, asPath } = useRouter();

  const [selectedAreas, setSelectedAreas] = useState<number[]>([]);

  const handleAreaFilterClick = (id: number) => {
    if (selectedAreas.includes(id)) {
      setSelectedAreas(state => {
        return [...state.filter(areaId => areaId !== id)];
      });
      logEvent.click(
        'lnch_area_filter_click',
        getPageLocation(asPath),
        'launches',
        {
          area_id: id
        }
      );
    } else {
      setSelectedAreas(state => {
        return [...state, id];
      });
      logEvent.click(
        'lnch_area_filter_remove',
        getPageLocation(asPath),
        'launches',
        {
          area_id: id
        }
      );
    }
  };

  const isMobile = useSelector((state: any) => state.app.isMobile);
  const webview = useSelector((state: any) => state.app.webview);

  const [pageSize, setPageSize] = useState(isMobile ? 12 : 24);

  const { extendedLaunches, status } = useFetchLaunches(
    launches,
    pageSize,
    selectedAreas
  );

  const handleLoadMore = () => {
    setPageSize(state => state + 12);
  };

  const breadcrumbItems = [
    {
      displayedName: t('all_launches'),
      path: '/new-launches'
    }
  ];

  return (
    <Container webview={webview === 'partners'}>
      {isMobile && webview !== 'partners' && (
        <LowerDetails entity={{}} entityType={EntityTypes.LAUNCH} />
      )}

      <div className="container-fluid">
        {webview !== 'partners' && (
          <Breadcrumbs itemsToShow={breadcrumbItems} />
        )}
        <LaunchesHeader locale={locale}>
          {isMobile ? (
            <h1 className="headline-1">
              {t('explore_new')} <span>{t('launches')}</span>
            </h1>
          ) : (
            <>
              <div className="header">
                <h1 className="headline-1">
                  {t('explore_new')} <span>{t('launches')}</span>
                </h1>
                <h2>{t('reserve_first')}</h2>
              </div>
              {webview !== 'partners' && (
                <div id="cta-container" className="cta-container">
                  <h3>{t('cta_title')}</h3>
                  <CTA
                    entity={{}}
                    entityType={EntityTypes.LAUNCH}
                    fillContainer
                  />
                </div>
              )}
            </>
          )}
        </LaunchesHeader>
        <LaunchesContainer>
          <LaunchAreaFilters
            areas={areas}
            selectedAreas={selectedAreas}
            handleClick={handleAreaFilterClick}
          />

          <div className="header">
            <h1 className="headline-1">{t('launching_soon')}</h1>

            {isMobile && (
              <span className="header-subtitle">
                {t('new_projects_available_now')}
              </span>
            )}
          </div>

          <AutoColumnsGrid minWidth="31.33%" rowsGap="16px" columnsGap="16px">
            {extendedLaunches?.results?.map(
              (launch: LaunchDTO, index: number) => (
                <LaunchPageCard
                  key={launch?.slug}
                  index={index}
                  entity={launch}
                  eventLocation="PriorityReservation"
                />
              )
            )}
          </AutoColumnsGrid>

          {isMobile && (
            <LoadMoreContainer
              loadedDataLength={
                extendedLaunches?.results?.length ?? launches?.results?.length
              }
              limit={
                extendedLaunches?.results?.length ??
                launches?.results?.length ??
                launches?.results?.length
              }
              isLoading={status === 'loading'}
              onChange={() => handleLoadMore()}
            />
          )}
          {webview !== 'partners' && (
            <TrendingProjects trendingProjects={trendingProjects} />
          )}
        </LaunchesContainer>

        {webview !== 'partners' && (
          <DescriptionBlock title={t('you_need_to_know')}>
            <p className="title">{t('info_title')}</p>
            <p>{t('info_p1')}</p>
            <p>{t('info_p2')}</p>
            <p>{t('info_p3')}</p>
            <p>{t('info_p4')}</p>
            <p className="headline">{t('subtitle_1')}</p>
            <p>{t('ul_header')}</p>
            <ul>
              <li>{t('li1_1')}</li>
              <li>{t('li1_2')}</li>
              <li>{t('li1_3')}</li>
            </ul>
            <p>{t('info_p5')}</p>
            <p className="headline">{t('subtitle_2')}</p>
            <p>{t('info_p6')}</p>
            <p>{t('info_p7')}</p>
            <p className="headline">{t('subtitle_3')}</p>
            <p>{t('li2_info')}</p>
            <ul>
              <li>{t('li2_1')}</li>
              <li>{t('li2_2')}</li>
              <li>{t('li2_3')}</li>
              <li>{t('li2_4')}</li>
              <li>{t('li2_5')}</li>
              <li>{t('li2_6')}</li>
              <li>{t('li2_7')}</li>
              <li>{t('li2_8')}</li>
              <li>{t('li2_9')}</li>
            </ul>
            <p>{t('li2_p')}</p>
            <p className="headline">{t('subtitle_4')}</p>
            <p>{t('li3_info')}</p>
            <ul>
              <li>{t('li3_1')}</li>
              <li>{t('li3_2')}</li>
              <li>{t('li3_3')}</li>
              <li>{t('li3_4')}</li>
              <li>{t('li3_5')}</li>
              <li>{t('li3_6')}</li>
              <li>{t('li3_7')}</li>
              <li>{t('li3_8')}</li>
              <li>{t('li3_9')}</li>
            </ul>
            <p className="headline">{t('subtitle_5')}</p>
            <p>{t('info_p8')}</p>
            <p>{t('info_p9')}</p>
            <p>{t('info_p10')}</p>
          </DescriptionBlock>
        )}
      </div>
    </Container>
  );
};

const Container = styled.div<ContainerProps>`
  padding-top: ${props => props.webview && '16px'};
  padding-bottom: ${props => props.webview && '16px'};

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    padding-top: ${props => props.webview && '8px'};
    padding-bottom: ${props => props.webview && '8px'};
  }
`;

const LaunchesHeader = styled.div<any>`
  display: flex;
  outline: 1px;
  border-bottom: solid 1px ${props => props.theme.light.colors.whitish};
  padding-bottom: 20px;
  justify-content: space-between;
  align-items: center;

  h1 {
    text-transform: uppercase;
    letter-spacing: ${props => (props.locale === 'en' ? '4.14px' : '0px')};

    span {
      color: #ccae80;
      font-size: 40px;
      vertical-align: middle;
      letter-spacing: 0px;
    }
  }

  h2 {
    text-transform: uppercase;
    letter-spacing: ${props => (props.locale === 'en' ? '2.34px' : '0px')};
    color: ${props => props.theme.light.colors.midGray};
    font-size: 18px;
  }

  .cta-container {
    background-color: #f8f8f8;
    padding: 16px;
    border-radius: ${props => props.theme.borderRadius};
    width: 415px;
    h3 {
      text-transform: uppercase;
      text-align: center;
      color: ${props => props.theme.light.colors.midGray};
      font-size: ${props => props.theme.font.sizes.s};
      margin-bottom: 8px;
    }
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    border-width: 0px;
    padding-bottom: 15px;

    h1 {
      letter-spacing: 0px;
      color: ${props => props.theme.light.colors.black};

      span {
        font-size: ${props => (props.locale === 'en' ? '32px' : '28px')};
        vertical-align: baseline;
      }
    }

    h2 {
      letter-spacing: 0px;
    }
  }
`;

const LaunchesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 32px;

  .header {
    display: flex;
    flex-direction: column;
    gap: 4px;
    h1 {
      color: #2c3138;
      font-size: 24px;
    }

    .header-subtitle {
      font-size: ${props => props.theme.font.sizes.xs};
      font-family: materialRegular;
      color: #697586;
    }
  }

  h3 {
    font-size: 24px;
    color: #00324c;
  }

  h4 {
    color: ${props => props.theme.light.colors.darkGrey};
    font-size: 14px;
    margin-top: 4px;
    margin-bottom: 16px;
  }

  .box-container {
    padding-bottom: 32px;
  }

  .box-container > div {
    padding: 16px 0px 0px 0px;
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    padding-top: 0;

    h1 {
      color: #2c3138;
      font-family: materialSemiBold;
    }

    h3 {
      font-size: 24px;
    }
    h4 {
      font-size: ${props => props.theme.font.sizes.xxs};
    }
  }
`;

export const getStaticProps = wrapper.getStaticProps(
  store =>
    async ({ params, locale }: GetStaticPropsContext) => {
      const [
        launches,
        trendingProjects,
        areas,
        footerLinks,
        configVariables,
        filterOptions
      ] = await Promise.all([
        LaunchesApis.getLaunches(
          {
            pagination: {
              page: 1,
              pageSize: params && params['device'] === 'mobile' ? 12 : 24
            },
            searchTerm: '',
            areaIds: []
          },
          locale
        ),
        LaunchesApis.getTrendingProjects(locale),
        getLaunchesAreas(locale),
        getFooterLinks(locale, 'launches'),
        ConfigsApis.getConfigs(locale),
        getFilterOptions(locale)
      ]);

      if (params?.platform === 'partners') {
        store.dispatch(setWebview(WEBVIEW.PARTNERS));
      }

      store.dispatch(setIsMobile(params?.device === 'mobile'));
      store.dispatch(setExperimentVariant(params?.variant as string));

      return {
        props: {
          ...(await serverSideTranslations(locale as string, [
            'launches',
            'common'
          ])),
          layout: {
            footerLinks,
            url: `${process.env.NEXT_PUBLIC_HOST}/new-launches`
          },
          launches: launches,
          areas: areas,
          filterOptions: JSON.stringify(filterOptions) || '',
          configVariables,
          trendingProjects
        },
        revalidate: 60 * 5
      };
    }
);

export const getStaticPaths = async () => {
  return { paths: StaticPagesUtility.getWithPlatform(), fallback: false };
};

export default Launches;
